import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;
const QUALIFICATIONS = ['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'];
const PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.gigzd.partner&pcampaignid=web_share';

export default function SharedJobApplyPage() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const [job, setJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(true);
  const [jobError, setJobError] = useState('');

  const [authStep, setAuthStep] = useState('form');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    mobile: '',
    qualification: '',
    course: '',
    referral_code: '',
    password: '',
    confirmPassword: ''
  });

  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [appliedMessage, setAppliedMessage] = useState('');
  const [loginDetails, setLoginDetails] = useState({ mobile: '', password: '' });

  const jobIdentifier = useMemo(() => Number(jobId), [jobId]);

  useEffect(() => {
    const fromUrl = (searchParams.get('referral') || searchParams.get('ref') || '').trim();
    if (fromUrl) {
      setRegisterForm((p) => ({ ...p, referral_code: fromUrl }));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchJob = async () => {
      setLoadingJob(true);
      setJobError('');
      try {
        const res = await fetch(`${API_URL}/api/candidate/jobs/${jobIdentifier}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.error || 'Failed to load job');
        }
        setJob(data.job);
      } catch (err) {
        setJobError(err.message || 'Failed to load job');
      } finally {
        setLoadingJob(false);
      }
    };

    if (!Number.isInteger(jobIdentifier) || jobIdentifier <= 0) {
      setLoadingJob(false);
      setJobError('Invalid job link');
      return;
    }
    fetchJob();
  }, [jobIdentifier]);

  const checkAlreadyApplied = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token || !Number.isInteger(jobIdentifier) || jobIdentifier <= 0) {
        setAlreadyApplied(false);
        return false;
      }
      const res = await fetch(`${API_URL}/api/candidate/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success || !Array.isArray(data.applications)) {
        return false;
      }
      const exists = data.applications.some((app) => Number(app.job_id) === jobIdentifier);
      setAlreadyApplied(exists);
      return exists;
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    const run = async () => {
      const exists = await checkAlreadyApplied();
      if (exists) {
        setShowAuth(false);
      }
    };
    run();
  }, [jobIdentifier]);

  useEffect(() => {
    const syncAuthUi = async () => {
      const token = localStorage.getItem('gogig_token');
      if (!token) return;
      setShowAuth(false);
      await checkAlreadyApplied();
    };
    syncAuthUi();
    const handleFocus = () => {
      syncAuthUi();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [jobIdentifier]);

  useEffect(() => {
    const checkIfAuthStillNeeded = async () => {
      if (!showAuth) return;
      const token = localStorage.getItem('gogig_token');
      if (!token) return;
      setShowAuth(false);
      await checkAlreadyApplied();
    };
    checkIfAuthStillNeeded();
  }, [showAuth]);

  const saveSession = (token, user) => {
    localStorage.setItem('gogig_token', token);
    localStorage.setItem('gogig_user', JSON.stringify(user || {}));
  };

  const applyToJob = async (withToastCredentials) => {
    if (alreadyApplied) return;
    setApplyLoading(true);
    setApplyError('');
    try {
      const token = localStorage.getItem('gogig_token');
      if (!token) {
        setPendingApply(true);
        setShowAuth(true);
        return;
      }
      const res = await fetch(`${API_URL}/api/candidate/jobs/${jobIdentifier}/apply`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem('gogig_token');
          localStorage.removeItem('gogig_user');
          setPendingApply(true);
          setShowAuth(true);
          throw new Error('Please register to continue');
        }
        throw new Error(data.error || 'Failed to apply');
      }

      setPendingApply(false);
      setShowAuth(false);
      setAlreadyApplied(true);
      if (withToastCredentials) {
        setLoginDetails(withToastCredentials);
      }
      setAppliedMessage('To track your application, download the Gogig Candidate mobile application from Play Store to know your application status.');
    } catch (err) {
      setApplyError(err.message || 'Failed to apply');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    const f = registerForm;
    if (!f.name || !f.email || !f.mobile || !f.qualification || !f.course || !f.password || !f.confirmPassword) {
      setAuthError('Please complete all required registration fields');
      return;
    }
    if (!/^\d{10}$/.test(f.mobile)) {
      setAuthError('Enter a valid 10-digit mobile number');
      return;
    }
    if (f.password !== f.confirmPassword) {
      setAuthError('Passwords do not match');
      return;
    }
    setAuthLoading(true);
    try {
      const payload = {
        name: f.name,
        email: f.email,
        mobile: f.mobile,
        qualification: f.qualification,
        course: f.course,
        password: f.password
      };
      const referralTrimmed = (f.referral_code || '').trim();
      if (referralTrimmed) {
        payload.referral_code = referralTrimmed;
      }
      const res = await fetch(`${API_URL}/api/candidate/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      setAuthStep('otp');
    } catch (err) {
      setAuthError(err.message || 'Registration failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!/^\d{6}$/.test(otp)) {
      setAuthError('Enter a valid 6-digit OTP');
      return;
    }
    setAuthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: registerForm.mobile, otp })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');
      saveSession(data.token || data.access, data.user || {});
      if (pendingApply) {
        await applyToJob({ mobile: registerForm.mobile, password: registerForm.password });
      }
    } catch (err) {
      setAuthError(err.message || 'OTP verification failed');
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)', padding: '24px 16px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Link to="/welcome" style={{ color: '#0d9488', textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
            Go to Home
          </Link>
          <div style={{ color: '#64748b', fontSize: 13 }}>GoGig Candidate Jobs</div>
        </div>

        {appliedMessage && (
          <div style={{ marginBottom: 14, padding: '14px 16px', borderRadius: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', boxShadow: '0 6px 18px rgba(16,185,129,0.15)' }}>
            {appliedMessage}
            <div style={{ marginTop: 10 }}>
              <a
                href={PLAYSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                  boxShadow: '0 8px 18px rgba(34,197,94,0.25)'
                }}
              >
                Download on Play Store
              </a>
            </div>
            {loginDetails.mobile && loginDetails.password && (
              <div style={{ marginTop: 8, fontWeight: 700 }}>
                Your login details — Mobile: {loginDetails.mobile}, Password: {loginDetails.password}
              </div>
            )}
          </div>
        )}
        {!appliedMessage && alreadyApplied && (
          <div style={{ marginBottom: 14, padding: '14px 16px', borderRadius: 12, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', boxShadow: '0 6px 18px rgba(16,185,129,0.15)' }}>
            To track your application, download the Gogig Candidate mobile application from Play Store to know your application status.
            <div style={{ marginTop: 10 }}>
              <a
                href={PLAYSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                  boxShadow: '0 8px 18px rgba(34,197,94,0.25)'
                }}
              >
                Download on Play Store
              </a>
            </div>
          </div>
        )}
        {applyError && <div style={{ marginBottom: 14, color: '#b91c1c' }}>{applyError}</div>}

        {loadingJob ? (
          <div>Loading job details...</div>
        ) : jobError ? (
          <div style={{ color: '#b91c1c' }}>{jobError}</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, boxShadow: '0 12px 30px rgba(15,23,42,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <img
                src={job.logo_url || 'https://via.placeholder.com/56?text=Logo'}
                alt="Company logo"
                style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover' }}
              />
              <div>
                <h2 style={{ margin: 0, color: '#0f172a' }}>{job.title}</h2>
                <div style={{ color: '#475569' }}>{job.company_name}</div>
              </div>
            </div>

            <div style={{ color: '#334155', marginBottom: 8 }}>
              Location: {job.location} | Age: {job.age_criteria} | Stipend: Rs.{job.stipend_min} - Rs.{job.stipend_max}
            </div>
            <div style={{ color: '#334155', marginBottom: 10 }}>Qualification: {job.qualification}</div>
            <div style={{ color: '#0f172a', marginBottom: 16, lineHeight: 1.65 }} dangerouslySetInnerHTML={{ __html: job.details || '' }} />

            <button
              type="button"
              onClick={() => applyToJob()}
              disabled={applyLoading || alreadyApplied}
              style={{
                padding: '11px 18px',
                border: 'none',
                borderRadius: 10,
                background: alreadyApplied ? '#94a3b8' : 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
                color: '#fff',
                fontWeight: 700,
                cursor: alreadyApplied ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 20px rgba(13,148,136,0.3)'
              }}
            >
              {applyLoading ? 'Applying...' : alreadyApplied ? 'Already Applied' : 'Apply'}
            </button>
            {alreadyApplied && (
              <div style={{ marginTop: 8, color: '#0f766e', fontWeight: 600 }}>
                You have already applied for this job.
              </div>
            )}
          </div>
        )}

        {showAuth && (
          <div style={{ marginTop: 20, background: '#fff', border: '1px solid #dbeafe', borderRadius: 16, padding: 20, boxShadow: '0 10px 26px rgba(59,130,246,0.08)' }}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Register to apply for this job</h3>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
              Create your candidate account below. After OTP verification, your application will be submitted automatically.
            </p>
            {authError && <div style={{ color: '#b91c1c', marginBottom: 10 }}>{authError}</div>}

            {authStep === 'form' ? (
              <form onSubmit={handleRegister} style={{ display: 'grid', gap: 10 }}>
                <input placeholder="Name *" value={registerForm.name} onChange={(e) => setRegisterForm((p) => ({ ...p, name: e.target.value }))} style={inputStyle} />
                <input placeholder="Email *" type="email" value={registerForm.email} onChange={(e) => setRegisterForm((p) => ({ ...p, email: e.target.value }))} style={inputStyle} />
                <input placeholder="Mobile Number *" value={registerForm.mobile} onChange={(e) => setRegisterForm((p) => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))} style={inputStyle} maxLength={10} />
                <select value={registerForm.qualification} onChange={(e) => setRegisterForm((p) => ({ ...p, qualification: e.target.value }))} style={inputStyle}>
                  <option value="">Select Qualification *</option>
                  {QUALIFICATIONS.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
                <input placeholder="Course *" value={registerForm.course} onChange={(e) => setRegisterForm((p) => ({ ...p, course: e.target.value }))} style={inputStyle} />
                <input
                  placeholder="Referral Code (optional)"
                  value={registerForm.referral_code}
                  onChange={(e) => setRegisterForm((p) => ({ ...p, referral_code: e.target.value }))}
                  style={inputStyle}
                />
                <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
                  Enter a referral code if someone referred you to GoGig.
                </p>
                <input type="text" placeholder="Password *" value={registerForm.password} onChange={(e) => setRegisterForm((p) => ({ ...p, password: e.target.value }))} style={inputStyle} />
                <input type="text" placeholder="Confirm Password *" value={registerForm.confirmPassword} onChange={(e) => setRegisterForm((p) => ({ ...p, confirmPassword: e.target.value }))} style={inputStyle} />
                <button type="submit" disabled={authLoading} style={primaryBtnStyle}>
                  {authLoading ? 'Please wait...' : 'Register & send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} style={{ display: 'grid', gap: 10 }}>
                <p style={{ margin: 0, color: '#475569', fontSize: 14 }}>
                  OTP sent to <strong>{registerForm.mobile}</strong>
                </p>
                <input
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={inputStyle}
                  maxLength={6}
                />
                <button type="submit" disabled={authLoading} style={primaryBtnStyle}>
                  {authLoading ? 'Verifying...' : 'Verify OTP and Apply'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAuthStep('form'); setOtp(''); setAuthError(''); }}
                  style={{
                    border: '1px solid #cbd5e1',
                    borderRadius: 10,
                    padding: '10px 14px',
                    fontSize: 14,
                    fontWeight: 600,
                    background: '#fff',
                    color: '#475569',
                    cursor: 'pointer'
                  }}
                >
                  Back to registration
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  border: '1.5px solid #cbd5e1',
  borderRadius: 10,
  fontSize: 15,
  color: '#0f172a',
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none'
};

const primaryBtnStyle = {
  border: 'none',
  borderRadius: 10,
  padding: '11px 14px',
  fontSize: 15,
  fontWeight: 700,
  color: '#fff',
  background: 'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
  cursor: 'pointer',
  boxShadow: '0 8px 18px rgba(13,148,136,0.25)'
};
